import { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { PlayCircleOutline } from "@mui/icons-material";
import type * as React from "react";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import { COLOGUARD_VIDEO_URL } from "./constants";

type HeroSectionProps = {
  onLearnMore: () => void;
};

const POSTER_TIME_SECONDS = 3.5;

const HeroSection: React.FC<HeroSectionProps> = ({ onLearnMore }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isSeekingPosterRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const canHover =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover)").matches;
  const showControls = isHovered || (isPlaying && !canHover);

  const showPosterFrame = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }

    isSeekingPosterRef.current = true;
    video.currentTime = Math.min(POSTER_TIME_SECONDS, video.duration);
  };

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    isSeekingPosterRef.current = false;
    video.currentTime = 0;
    void video.play();
  };

  return (
    <Box
      component="section"
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: { md: 4, xs: 3 },
        width: "100%"
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: { lg: "row", xs: "column" },
          gap: { lg: 6, xs: 4 }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: 2.5,
            minWidth: 0
          }}
        >
          <Typography component="h1" variant="h4_black" sx={{ m: 0 }}>
            It&apos;s Time for
            <br />
            Colon Cancer Screening
          </Typography>
          <Typography component="p" variant="body_black" sx={{ m: 0 }}>
            It&apos;s important to stay up-to-date with colon cancer screening.
            Based on the information you&apos;ve provided, you may be due.
          </Typography>
          <GLButton
            buttonType="primary"
            onClick={onLearnMore}
            sx={{ alignSelf: { lg: "flex-start", xs: "stretch" } }}
            text="Learn More"
          />
        </Box>

        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            aspectRatio: "16 / 10",
            borderRadius: "16px",
            flex: { lg: "0 1 48%", xs: "none" },
            minHeight: { xs: 200, md: 280 },
            overflow: "hidden",
            position: "relative",
            width: { lg: "auto", xs: "100%" }
          }}
        >
          <Box
            aria-label="Cologuard colon cancer screening video"
            component="video"
            controls={showControls}
            onEnded={() => {
              setIsPlaying(false);
              showPosterFrame();
            }}
            onLoadedMetadata={showPosterFrame}
            onPause={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onSeeked={() => {
              if (isSeekingPosterRef.current) {
                isSeekingPosterRef.current = false;
                videoRef.current?.pause();
              }
            }}
            playsInline
            preload="auto"
            ref={videoRef}
            src={COLOGUARD_VIDEO_URL}
            sx={{
              display: "block",
              height: "100%",
              objectFit: "cover",
              width: "100%"
            }}
          />
          {!isPlaying && (
            <Box
              aria-label="Play video"
              component="button"
              onClick={handlePlayClick}
              type="button"
              sx={{
                alignItems: "center",
                backgroundColor: "transparent",
                border: 0,
                cursor: "pointer",
                display: "flex",
                height: "100%",
                justifyContent: "center",
                left: 0,
                padding: 0,
                position: "absolute",
                top: 0,
                width: "100%"
              }}
            >
              <PlayCircleOutline
                sx={{
                  color: COLORS.WHITE,
                  filter: "drop-shadow(0 2px 8px rgba(0, 0, 0, 0.35))",
                  fontSize: { md: 72, xs: 56 }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
